import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import {
    DataGrid,
    Column,
    Scrolling,
    Selection,
    Toolbar,
    Lookup,
} from "devextreme-react/data-grid";
import axios from "axios";

const TITLE = "Select Cost Components";
const BASE = "om/SalesOrderDetailOperations";

function SelectCostComponentsPopup(props) {
    const visible = props.showPopup.visible === true;
    const headerRowId = props.showPopup.headerRowId;
    const initialDetailRowId = props.showPopup.detailRowId;

    const [showCurrentOnly, setShowCurrentOnly] = useState(true);
    const [parentRows, setParentRows] = useState([]);
    const [selectedDetailRowId, setSelectedDetailRowId] = useState(null);
    const [vendorCRRows, setVendorCRRows] = useState([]);
    const [assignedRows, setAssignedRows] = useState([]);
    const [error, setError] = useState(null);

    const token = props.oidc?.user?.access_token;
    const baseUrl = window._env_?.REACT_APP_APIS || "";

    const loadParentGrid = useCallback(async () => {
        if (!visible || !headerRowId) return;
        props.setLoadPanelVisible(true);
        try {
            const res = await axios.get(`${baseUrl}/${BASE}/GetCostComponentsParentGrid`, {
                params: {
                    headerRowId,
                    showCurrentOnly,
                    detailRowId: showCurrentOnly && initialDetailRowId ? initialDetailRowId : undefined,
                },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const data = res.data || [];
            setParentRows(Array.isArray(data) ? data : []);
            if (data.length > 0 && !selectedDetailRowId) {
                const first = data[0];
                setSelectedDetailRowId(first.rowId || first.RowId);
            }
        } catch (e) {
            setParentRows([]);
        } finally {
            props.setLoadPanelVisible(false);
        }
    }, [props.showPopup.headerRowId, props.showPopup.detailRowId, showCurrentOnly]);

    const loadChildGrids = useCallback(async () => {
        if (!selectedDetailRowId || !visible) {
            setVendorCRRows([]);
            setAssignedRows([]);
            return;
        }
        props.setLoadPanelVisible(true);
        try {
            const [vcrRes, asnRes] = await Promise.all([
                axios.get(`${baseUrl}/${BASE}/GetVendorCRCostComponents`, {
                    params: { detailRowId: selectedDetailRowId },
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                }),
                axios.get(`${baseUrl}/${BASE}/GetAssignedCostComponents`, {
                    params: { detailRowId: selectedDetailRowId },
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                }),
            ]);
            setVendorCRRows(Array.isArray(vcrRes.data) ? vcrRes.data : []);
            setAssignedRows(Array.isArray(asnRes.data) ? asnRes.data : []);
        } catch (e) {
            setVendorCRRows([]);
            setAssignedRows([]);
        } finally {
            props.setLoadPanelVisible(false);
        }
    }, [selectedDetailRowId, visible, token, baseUrl]);

    useEffect(() => {
        loadParentGrid();
    }, [props.showPopup.headerRowId, props.showPopup.detailRowId, loadParentGrid]);

    useEffect(() => {
        loadChildGrids();
    }, [selectedDetailRowId]);

    const onParentSelectionChanged = (e) => {
        const row = e.selectedRowsData && e.selectedRowsData[0];
        const id = row ? (row.rowId ?? row.RowId) : null;
        setSelectedDetailRowId(id);
    };

    const onVendorCRRowDblClick = async (e) => {
        const row = e.data;
        if (!row || !selectedDetailRowId) return;
        const rowId = row.rowId ?? row.RowId;
        props.setLoadPanelVisible(true);
        try {
            await axios.post(
                `${baseUrl}/${BASE}/AssignCostComponent`,
                null,
                { params: { detailRowId: selectedDetailRowId, vendorCRRowId: rowId }, headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            await loadChildGrids();
        } catch (err) {
        } finally {
            props.setLoadPanelVisible(false);
        }
    };

    const onAssignedRowDblClick = async (e) => {
        const row = e.data;
        if (!row || !selectedDetailRowId) return;
        const rowId = row.rowId ?? row.RowId;
        props.setLoadPanelVisible(true);
        try {
            await axios.post(
                `${baseUrl}/${BASE}/UnassignCostComponent`,
                null,
                { params: { detailRateRowId: rowId }, headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            await loadChildGrids();
        } catch (err) {
        } finally {
            props.setLoadPanelVisible(false);
        }
    };

    const onShowAllClick = () => {
        setShowCurrentOnly((prev) => !prev);
    };

    const onClose = async () => {
        props.setShowPopup((prev) => ({ ...prev, visible: false }));
        setParentRows([]);
        setSelectedDetailRowId(null);
        setVendorCRRows([]);
        setAssignedRows([]);
    };

    const parentData = parentRows;
    const vendorCRData = vendorCRRows;
    const assignedData = assignedRows;

    return (
        <>
            {visible && (
                <Popup
                    visible={true}
                    onHiding={onClose}
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    showCloseButton={true}
                    showTitle={true}
                    title={props.showPopup.title ?? TITLE}
                    container="#root"
                    width="100vw"
                    height="100vh"
                >
                    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 8 }}>
                        <div style={{ flex: "0 0 220px", minHeight: 200 }}>
                            <DataGrid
                                dataSource={parentData}
                                keyExpr="RowId"
                                showBorders={true}
                                columnAutoWidth={true}
                                height="100%"
                                selectedRowKeys={selectedDetailRowId ? [selectedDetailRowId] : []}
                                onSelectionChanged={onParentSelectionChanged}
                            >
                                <Selection mode="single" />
                                <Scrolling mode="standard" />
                                <Column dataField="Warehouse" caption="Warehouse" />
                                <Column dataField="Item" caption="Item" />
                                <Column dataField="ItemDescription" caption="Item Description" />
                                <Column dataField="Lot" caption="Lot" />
                                <Column dataField="LotDescription" caption="Lot Description" />
                                <Column dataField="Ordered" caption="Ordered" dataType="number" format={{ type: 'fixedPoint', precision: 2, useGrouping: true }} />
                                <Column dataField="Shipped" caption="Shipped" dataType="number" format={{ type: 'fixedPoint', precision: 2, useGrouping: true }} />
                                <Column dataField="BackOrder" caption="BackOrder" dataType="number" format={{ type: 'fixedPoint', precision: 2, useGrouping: true }} />
                                <Column dataField="UnitPrice" caption="Unit Price" dataType="number" format={{ type: 'fixedPoint', precision: 2, useGrouping: true }} />
                                <Column dataField="Amount" caption="Amount" dataType="number" format={{ type: 'fixedPoint', precision: 2, useGrouping: true }} />
                            </DataGrid>
                        </div>
                        <div style={{ flex: "1 1 200px", display: "flex", gap: 8, minHeight: 180 }}>
                            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <div style={{ fontWeight: "bold", marginBottom: 4 }}>Vendor CR Cost Components (doubleclick to assign)</div>
                                <DataGrid
                                    dataSource={vendorCRData}
                                    keyExpr="RowId"
                                    showBorders={true}
                                    columnAutoWidth={true}
                                    height="100%"
                                    onRowDblClick={onVendorCRRowDblClick}
                                >
                                    <Scrolling mode="standard" />
                                    <Column dataField="Vendor" caption="Vendor" />
                                    <Column dataField="ComponentCode" caption="Component Code" />
                                    <Column dataField="Group" caption="Group" dataType="number" />
                                    <Column dataField="DateFrom" caption="Date From" dataType="date" format="shortDate" />
                                    <Column dataField="DateTo" caption="Date To" dataType="date" format="shortDate" />
                                </DataGrid>
                            </div>
                            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <div style={{ fontWeight: "bold", marginBottom: 4 }}>Assigned Cost Components (doubleclick to remove)</div>
                                <DataGrid
                                    dataSource={assignedData}
                                    keyExpr="RowId"
                                    showBorders={true}
                                    columnAutoWidth={true}
                                    height="100%"
                                    onRowDblClick={onAssignedRowDblClick}
                                >
                                    <Scrolling mode="standard" />
                                    <Column dataField="ComponentCode" caption="Component Code" />
                                    <Column dataField="Group" caption="Group" dataType="number" />
                                    <Column dataField="RateType" caption="Rate Type" >
                                    <Lookup 
                                        dataSource={[{id: "U", text: "Per Unit"}]}
                                        valueExpr="id"
                                        displayExpr="text"
                                    />
                                    </Column>
                                    <Column dataField="Rate" caption="Rate" dataType="number" format={{ type: 'fixedPoint', precision: 2, useGrouping: true }} />
                                    <Column dataField="Quantity" caption="Quantity" dataType="number" format={{ type: 'fixedPoint', precision: 2, useGrouping: true }} />
                                    <Column dataField="Amount" caption="Amount" dataType="number" format={{ type: 'fixedPoint', precision: 2, useGrouping: true }} />
                                </DataGrid>
                            </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 8, borderTop: "1px solid #ddd" }}>
                            <Button text={showCurrentOnly ? "Show All" : "Show Current"} onClick={onShowAllClick} />
                            <Button text="Close" type="default" onClick={onClose} />
                        </div>
                    </div>
                </Popup>
            )}
        </>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(SelectCostComponentsPopup);
